import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants';
import { Button } from '@/components/ui';
import { useModalStore } from '@/stores';
import styles from './ProviderModals.module.css';

interface Provider {
  id: string;
  nombre_comercial: string;
}

interface DeleteProviderModalProps {
  provider: Provider;
  onSuccess?: () => void;
}

export function DeleteProviderModal({ provider, onSuccess }: DeleteProviderModalProps) {
  const { closeModal } = useModalStore();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      await api.delete(`${API_ROUTES.providers}${provider.id}/`);
    },
    onSuccess: () => {
      toast.success('Provider deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      queryClient.invalidateQueries({ queryKey: ['provider-stats'] });
      onSuccess?.();
      closeModal();
    },
    onError: (error: unknown) => {
      const apiError = error as { response?: { data?: { error?: string } } };
      const errorMessage = apiError.response?.data?.error || 'Error deleting provider';
      toast.error(errorMessage);
    },
  });

  return (
    <div className={styles.deleteContainer}>
      <div className={styles.deleteIconContainer}>
        <FontAwesomeIcon icon={faExclamationTriangle} className={styles.deleteIcon} />
      </div>

      <h3 className={styles.deleteTitle}>Delete Provider</h3>

      <p className={styles.deleteMessage}>
        Are you sure you want to delete the provider <strong>{provider.nombre_comercial}</strong>?
      </p>

      <p className={styles.deleteSubtext}>
        This action cannot be undone. All associated services and data will be removed.
      </p>

      <div className={styles.deleteActions}>
        <Button variant="secondary" onClick={closeModal} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button variant="danger" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
          {mutation.isPending ? 'Deleting...' : 'Delete'}
        </Button>
      </div>
    </div>
  );
}
